import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider
} from "@mui/material";
import localize from "../../../common/localize";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const toSeconds = ({ days = 0, hours = 0, minutes = 0, seconds = 0 } = {}) =>
  days * 86400 + hours * 3600 + minutes * 60 + seconds;

const formatShort = (seconds) => {
  if (seconds >= 86400) return `${Math.floor(seconds / 86400)}d`;
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m`;
  return `${seconds}s`;
};

const formatFull = (seconds) => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  return `${d} día(s), ${h} hora(s), ${m} minuto(s), ${s} segundo(s)`;
};

const createData = (fieldName, fieldValue, color) => ({
    fieldName,
    fieldValue,
    color,
  });

const eventInfoData = (chartData) => {
    if (chartData?.data) {
      const dataTable = chartData.data.map(
        (x) => createData(`${x.label}`, x.timeElapsed, x.backColor),
        "normal",
      );

      return dataTable;
    }

    return [createData(localize.total, undefined, "event_data")];
  };

const TimeDistributionCard = ({ data = [] }) => {
  const convertedData = eventInfoData(data);

  const processed = convertedData.map((item) => {
    const seconds = toSeconds(item.fieldValue);
    return {
      name: item.fieldName,
      seconds,
      color: item.color || "#8884d8"
    };
  });

  const total = processed.reduce((acc, i) => acc + i.seconds, 0);

  const chartData = {
    labels: [""],
    datasets: processed.map((item) => ({
      label: item.name,
      data: [item.seconds],
      backgroundColor: item.color,
      barPercentage: 0.4,
      categoryPercentage: 1.0,
    })),
  };

  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const item = processed[ctx.datasetIndex];
            return item ? `${item.name}: ${formatFull(item.seconds)}` : "";
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        display: false,
      },
      y: {
        stacked: true,
        display: false,
      },
    },
  };

  return (
    <Card className="w-full" sx={{ borderRadius: 4 }}>
      <CardContent>

        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {localize.time_distribution}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          {formatFull(total)}
        </Typography>

        <Box sx={{ height: 60 }}>
          {processed.length > 0 && total > 0 ? (
            <Bar data={chartData} options={chartOptions} />
          ) : null}
        </Box>

        <Box display="flex" justifyContent="space-between" mt={1}>
          <Typography variant="caption">0s</Typography>
          <Typography variant="caption">{formatShort(total)}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          {processed.map((item, index) => {
            const percent = total
              ? Math.round((item.seconds / total) * 100)
              : 0;

            return (
              <Box
                key={index}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1.5}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 1,
                      background: item.color
                    }}
                  />
                  <Typography variant="body2">
                    {item.name}
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatShort(item.seconds)} - {percent}%
                </Typography>
              </Box>
            );
          })}
        </Box>

      </CardContent>
    </Card>
  );
};

export default TimeDistributionCard;
